import { AutoComplete, Input, InputNumber, Tooltip } from 'antd';
import React, { KeyboardEvent, useState, useEffect, useRef } from 'react';
import { ShieldAlert, X } from 'lucide-react';

import { BasicProduct } from '@/types';
import { useTranslation } from 'react-i18next';
import ProductSubmitButton from './ProductSubmitButton';
import { cleanNumber, formatWithThousandSeparator } from '@/utils';
import useOrderTypeStore, { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import { searchProductCatalog } from '@/api/productCatalogService';

interface ProductFieldErrors {
  title?: boolean;
  price?: boolean;
  quantity?: boolean;
}

interface ProductInputsRowProps {
  product: BasicProduct;
  handleChangeProductKey: (field: keyof BasicProduct, value: string) => void;
  handleChangeProductKeyBatch?: (updates: Partial<Record<keyof BasicProduct, string>>) => void;
  handleSubmit: () => void;
  buttonIcon?: React.ElementType;
  isPrimaryButton?: boolean;
  disableButton?: boolean;
  hideButton?: boolean;
  onProductValueChanged?: () => void;
  autoFocus?: boolean;
  errors?: ProductFieldErrors;
  onFieldBlur?: (field: keyof BasicProduct) => void;
  showDeleteButton?: boolean;
  onDelete?: () => void;
  showQuantity?: boolean;
  disableCatalog?: boolean;
}

const ProductInputsRow: React.FC<ProductInputsRowProps> = ({
  product,
  buttonIcon: ButtonIcon,
  isPrimaryButton,
  disableButton,
  hideButton,
  handleChangeProductKey,
  handleChangeProductKeyBatch,
  handleSubmit,
  onProductValueChanged,
  autoFocus,
  errors,
  onFieldBlur,
  showDeleteButton,
  onDelete,
  showQuantity,
  disableCatalog,
}) => {
  const { t } = useTranslation();
  const { orderType } = useOrderTypeStore();

  const [catalogOptions, setCatalogOptions] = useState<{ value: string; label: React.ReactNode; catalogItem?: { uid: string; price: number | null; quantity: number; is_fragile: boolean } }[]>([]);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleCatalogSearch = (text: string) => {
    handleChangeProductKey('title', text);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (text.length < 2) {
      setCatalogOptions([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await searchProductCatalog(text);
        const items = Array.isArray(res?.object) ? res.object : [];
        setCatalogOptions(
          (items as { uid: string; title: string; price: number | null; quantity: number; category?: string | null; is_fragile?: boolean }[]).map((item) => ({
            value: item.title,
            label: (
              <div className='flex justify-between items-center gap-2'>
                <span className='truncate'>{item.title}</span>
                <div className='flex items-center gap-1.5 shrink-0'>
                  {item.is_fragile && (
                    <span className='text-[10px] font-medium text-[rgb(217,119,6)]'>⚠</span>
                  )}
                  <span className='text-xs text-sd-muted-foreground'>
                    {item.price !== null ? `${item.price.toLocaleString()} IQD` : '—'}
                  </span>
                </div>
              </div>
            ),
            catalogItem: { uid: item.uid, price: item.price, quantity: item.quantity, is_fragile: item.is_fragile ?? false },
          })),
        );
      } catch {
        setCatalogOptions([]);
      }
    }, 300);
  };

  const handleCatalogSelect = (_value: string, option: typeof catalogOptions[number]) => {
    if (option.catalogItem) {
      // Batch all catalog fields in a single store write to avoid stale-state race
      // Only set price if the catalog product has one — don't overwrite with empty
      const updates: Partial<Record<keyof BasicProduct, string>> = {
        title: _value,
        product_catalog_uid: option.catalogItem.uid,
        is_fragile: option.catalogItem.is_fragile ? 'true' : 'false',
      };
      if (option.catalogItem.price !== null) {
        updates.price = String(option.catalogItem.price);
      }

      if (handleChangeProductKeyBatch) {
        handleChangeProductKeyBatch(updates);
      } else {
        for (const [field, val] of Object.entries(updates)) {
          handleChangeProductKey(field as keyof BasicProduct, val);
        }
      }
      onProductValueChanged?.();
    }
  };

  useEffect(() => {
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, []);

  const handleEnterKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();

      if (isPrimaryButton) handleSubmit();
    }
  };

  // Determine min value based on orderType
  const getMinValue = () => {
    switch (orderType) {
      case OrderTypeEnum.NORMAL:
        return 0; // Positive and Zero values for normal orders
      default:
        return -Infinity; // Allow any negative value
    }
  };

  const minValue = getMinValue();

  const isFromCatalog = !!product.product_catalog_uid;

  const handleClearCatalogProduct = () => {
    if (handleChangeProductKeyBatch) {
      handleChangeProductKeyBatch({
        title: '',
        price: '',
        product_catalog_uid: '',
        is_fragile: 'false',
      });
    } else {
      handleChangeProductKey('title', '');
      handleChangeProductKey('price', '');
      handleChangeProductKey('product_catalog_uid', '');
      handleChangeProductKey('is_fragile', 'false');
    }
    onProductValueChanged?.();
  };

  return (
    <div className='flex gap-2 items-start'>
      <div className='flex-1 min-w-0 relative'>
        {disableCatalog ? (
          <Input
            autoFocus={autoFocus}
            placeholder={String(t('product_name'))}
            value={product.title}
            status={errors?.title ? 'error' : undefined}
            onChange={(e) => handleChangeProductKey('title', e.target.value)}
            onKeyDown={handleEnterKey}
            onBlur={() => onFieldBlur?.('title')}
            className={`w-full !rounded-md !h-[40px] !bg-sd-input-bg ${errors?.title ? '!border-sd-destructive' : '!border-sd-input'}`}
          />
        ) : (
          <>
            <AutoComplete
              autoFocus={autoFocus}
              placeholder={String(t('product_name'))}
              value={product.title}
              status={errors?.title ? 'error' : undefined}
              options={catalogOptions}
              onSearch={handleCatalogSearch}
              onSelect={handleCatalogSelect}
              onChange={(val) => handleChangeProductKey('title', val)}
              onKeyDown={handleEnterKey}
              onBlur={() => {
                onFieldBlur?.('title');
                setCatalogOptions([]);
              }}
              allowClear={false}
              className={`w-full [&_.ant-select-selector]:!border [&_.ant-select-selector]:!border-solid [&_.ant-select-selector]:!rounded-md [&_.ant-select-selector]:!h-[40px] [&_.ant-select-selector]:!bg-sd-input-bg ${isFromCatalog && product.title ? '[&_.ant-select-selector]:!pe-8' : ''} ${errors?.title ? '[&_.ant-select-selector]:!border-sd-destructive' : '[&_.ant-select-selector]:!border-sd-input'}`}
            />
            {isFromCatalog && product.title && (
              <button
                type='button'
                onClick={handleClearCatalogProduct}
                className='absolute top-1/2 -translate-y-1/2 end-2 z-10 flex items-center justify-center w-4 h-4 rounded-full bg-[rgb(190,190,188)] hover:bg-[rgb(160,160,158)] text-white transition-colors cursor-pointer'
                title={String(t('clear', { defaultValue: 'Clear' }))}
              >
                <X className='w-2.5 h-2.5' strokeWidth={3} />
              </button>
            )}
          </>
        )}
      </div>
      {showQuantity && (
        <div className='w-16 flex-shrink-0'>
          <InputNumber
            min={1}
            max={99999999}
            parser={cleanNumber}
            placeholder={String(t('qty'))}
            value={product.quantity}
            formatter={(value) => value?.toString() || ''}
            className={`border border-solid relative custom-input-number flex items-center w-full text-center rounded-md h-[40px] bg-sd-input-bg border-sd-input`}
            onChange={(value) => handleChangeProductKey('quantity', value?.toString() || '1')}
            onKeyDown={handleEnterKey}
          />
        </div>
      )}
      <div className='w-36 flex-shrink-0'>
        <InputNumber
          min={minValue}
          maxLength={10}
          formatter={(value) => (value ? formatWithThousandSeparator(value) : '')}
          parser={(displayValue: string | undefined) =>
            cleanNumber(displayValue, orderType === OrderTypeEnum.NORMAL)
          }
          placeholder={t('price_iqd')}
          value={product.price}
          status={errors?.price ? 'error' : undefined}
          onChange={(value) => handleChangeProductKey('price', value?.toString() || '')}
          onKeyDown={handleEnterKey}
          onBlur={() => {
            onFieldBlur?.('price');
            onProductValueChanged?.();
          }}
          className={`border border-solid relative custom-input-number flex items-center w-full rounded-md h-[40px] bg-sd-input-bg ${errors?.price ? 'border-sd-destructive' : 'border-sd-input'}`}
        />
      </div>
      {!disableCatalog && (
        <Tooltip title={String(t(product.is_fragile ? 'fragile' : 'not_fragile', { defaultValue: product.is_fragile ? 'Fragile' : 'Not fragile' }))}>
          {isFromCatalog ? (
            <div
              className={`flex items-center justify-center w-[40px] h-[40px] rounded-md border border-solid shrink-0 transition-colors ${
                product.is_fragile
                  ? 'border-[rgb(245,158,11)] bg-[rgba(245,158,11,0.1)] text-[rgb(217,119,6)]'
                  : 'border-sd-input bg-sd-input-bg text-sd-muted-foreground opacity-40'
              }`}
            >
              <ShieldAlert className='h-4 w-4' />
            </div>
          ) : (
            <button
              type='button'
              onClick={() => {
                handleChangeProductKey('is_fragile', product.is_fragile ? 'false' : 'true');
                onProductValueChanged?.();
              }}
              className={`flex items-center justify-center w-[40px] h-[40px] rounded-md border border-solid shrink-0 cursor-pointer transition-colors ${
                product.is_fragile
                  ? 'border-[rgb(245,158,11)] bg-[rgba(245,158,11,0.1)] text-[rgb(217,119,6)]'
                  : 'border-sd-input bg-sd-input-bg text-sd-muted-foreground hover:bg-sd-muted'
              }`}
            >
              <ShieldAlert className='h-4 w-4' />
            </button>
          )}
        </Tooltip>
      )}
      {showDeleteButton && (
        <button
          type='button'
          onClick={onDelete}
          className='flex items-center justify-center w-[40px] h-[40px] rounded-md border border-solid border-sd-input bg-sd-input-bg text-sd-muted-foreground hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-colors cursor-pointer shrink-0'
        >
          <X className='h-4 w-4' />
        </button>
      )}
      {!hideButton && ButtonIcon && (
        <ProductSubmitButton
          buttonIcon={ButtonIcon}
          handleSubmit={handleSubmit}
          disableButton={disableButton}
          isPrimaryButton={isPrimaryButton}
        />
      )}
    </div>
  );
};

export default ProductInputsRow;
